// Создание сделки — ПЕРВАЯ ЗАПИСЬ, которую CRM делает в боевой Bitrix.
//
// Всё остальное в этой CRM только читает. Здесь читать нечего: человек нажимает кнопку, и в
// Bitrix появляется сделка, на добавление которой подписан бизнес-процесс 685 «Процесс для
// общей сделки» — стейт-машина из 14 состояний, раздающая задания живым людям
// (`БИЗНЕС-ПРОЦЕССЫ_Bitrix.md` §1, §6.4). Отменить запуск процесса нельзя, удалить сделку
// из CRM тоже нельзя. Отсюда три правила этого экрана:
//
//   1. ДВА ШАГА, А НЕ ОДНА КНОПКА. Сначала «Проверить» — пробная отправка (`dry`), при
//      которой ядро проверяет то же, что при настоящей записи, но в Bitrix не пишет.
//      Только потом — подтверждение с явным предупреждением и отдельной галочкой.
//   2. НИ ОДНОГО ПОЛЯ, КОТОРОЕ ПИШУТ БИЗНЕС-ПРОЦЕССЫ. Что закрыто — знает ядро
//      (`check_blocked`, блок-лист из bp_fields.py), и говорит об этом в справочнике
//      `/fields`: `readonly` + причина. Форма спрашивает справочник, а не свой список,
//      и у выключенного поля показывает причину словами ядра.
//   3. «СОЗДАНО» — ТОЛЬКО ПОСЛЕ ПЕРЕЧИТКИ ИЗ ЯДРА. Ответ «ok» от Bitrix этого не
//      доказывает: запись в Postgres внутри ядра при ошибке только пишет в лог, а карточка
//      читает ядро. Перечитка живёт в useCreateDeal (api/hooks.ts).
//
// Своих правил записи здесь нет: что можно писать, во что превратится воронка и какие поля
// уйдут в Bitrix — решает ядро, экран это показывает.

import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useCreateDeal, useDealDryRun, useFields, useRefs, useRightsApi } from '../api/hooks'
import { EntityPicker } from '../components/EntityPicker'
import { QueryState } from '../components/QueryState'
import { DEAL_VESSEL_FIELD, entityPath } from '../lib/entities'
import type { EntityRef } from '../lib/entities'
import { fmtMoney } from '../lib/format'
import { toNumber } from '../lib/money'

/**
 * Воронка, в которую ядро кладёт ЛЮБУЮ созданную с портала сделку: `fields['CATEGORY_ID']='4'`
 * в `deal_write` (core_api.py, решение владельца 14.07.2026). Выбора здесь нет и быть не
 * может: `CATEGORY_ID` входит в блок-лист ядра (смена воронки сбрасывает стадию), и запрос
 * с этим полем ядро отвергнет целиком.
 *
 * Номер зашит только как подпись ДО проверки. После пробной отправки берём воронку из ответа
 * ядра — оно и есть источник правды, а не эта строка.
 */
const CORE_CATEGORY_ID = '4'

/** Bitrix обрезает название сделки; режем сами, чтобы человек увидел это до записи. */
const TITLE_MAX = 255

/**
 * Поля, которые экран отправляет в ядро. Список нужен целиком, а не по одному:
 * перед показом формы каждое проверяется по справочнику `/fields` — не закрыто ли оно
 * блок-листом. Закрытое поле не должно оказаться ни во вводе, ни в запросе.
 */
const WRITTEN_FIELDS: Array<{ code: string; label: string; why: string }> = [
  { code: 'TITLE', label: 'Название', why: 'обязательное поле, его же требует ядро' },
  { code: 'COMPANY_ID', label: 'Компания', why: 'без неё сделку не найти ни в одном разрезе' },
  { code: 'ASSIGNED_BY_ID', label: 'Ответственный', why: 'на нём висят деньги и задания БП' },
  { code: 'OPPORTUNITY', label: 'Сумма', why: 'вход в расчёты; БП её потом пересчитает' },
  { code: 'CURRENCY_ID', label: 'Валюта', why: 'всегда RUB — как при создании с портала 55' },
  { code: DEAL_VESSEL_FIELD, label: 'Судно', why: 'необязательно, но для этой отрасли частая связь' },
  { code: 'OPENED', label: 'Доступна для всех', why: 'всегда «да»: иначе люди, которым БП раздаст задания, сделку не увидят' },
]

interface Form {
  title: string
  company: EntityRef | null
  vessel: EntityRef | null
  assigned: string
  opportunity: string
}

const EMPTY: Form = { title: '', company: null, vessel: null, assigned: '', opportunity: '' }

export default function NewDeal() {
  const navigate = useNavigate()
  const rights = useRightsApi()
  const refs = useRefs()
  // Справочник полей сделки: отсюда и человеческие названия, и пометки «писать нельзя».
  const f = useFields('deal')

  const [form, setForm] = useState<Form>(EMPTY)
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((p) => ({ ...p, [k]: v }))

  // Согласие на запись в боевой Bitrix. Сбрасывается при любой правке формы: галочка
  // относится к тому, что человек прочитал в подтверждении, а не к экрану вообще.
  const [agreed, setAgreed] = useState(false)

  const dry = useDealDryRun()
  const create = useCreateDeal()

  // ─── что ядро запрещает писать ───
  // Не свой список, а справочник: пополнят блок-лист в ядре — форма закроет поле сама.
  const blockedHere = useMemo(
    () => WRITTEN_FIELDS.filter((w) => f.readonly(w.code)),
    [f],
  )
  // Все закрытые поля сделки — для честной сводки «чего здесь нет и почему».
  // Считаем по справочнику, а не по числу из документации: справочник — это состояние
  // ядра сейчас, а число в документе стареет.
  const blockedAll = useMemo(() => {
    const byReason = new Map<string, string[]>()
    for (const { code, meta } of f.list) {
      if (!meta.readonly) continue
      const reason = f.readonlyReason(code)
      const list = byReason.get(reason)
      if (list) list.push(code)
      else byReason.set(reason, [code])
    }
    return [...byReason.entries()]
      .map(([reason, codes]) => ({ reason, codes }))
      .sort((a, b) => b.codes.length - a.codes.length)
  }, [f])

  // ─── проверки формы ───
  const title = form.title.trim()
  const amount = toNumber(form.opportunity)
  const problems: string[] = []
  if (!title) problems.push('не заполнено название сделки')
  if (title.length > TITLE_MAX) problems.push(`название длиннее ${TITLE_MAX} символов — Bitrix его обрежет`)
  if (!form.company) problems.push('не выбрана компания')
  if (!form.assigned) problems.push('не выбран ответственный')
  if (form.opportunity.trim() === '') problems.push('не заполнена сумма')
  else if (amount === null) problems.push('сумма не похожа на число')
  else if (amount < 0) problems.push('сумма отрицательная')
  // Форма собрана из полей, которые ядро закрыло — тогда её нельзя показывать вовсе.
  if (blockedHere.length) {
    problems.push('ядро закрыло для записи поля, из которых состоит эта форма: '
      + blockedHere.map((b) => b.label).join(', '))
  }
  const canCheck = problems.length === 0

  /** Поля ровно в том виде, в каком они уйдут в ядро. Одно место — и проверка, и запись. */
  const fields = useMemo((): Record<string, unknown> => ({
    TITLE: title.slice(0, TITLE_MAX),
    COMPANY_ID: form.company?.id ?? '',
    ASSIGNED_BY_ID: form.assigned,
    OPPORTUNITY: amount ?? 0,
    CURRENCY_ID: 'RUB',
    OPENED: 'Y',
    // Судно необязательно: пустое поле в запрос не кладём вовсе, чтобы не записать «0».
    ...(form.vessel ? { [DEAL_VESSEL_FIELD]: form.vessel.id } : {}),
  }), [title, form.company, form.assigned, form.vessel, amount])

  /**
   * Поля из ответа пробной отправки — то, что ядро СОБИРАЕТСЯ записать (с дописанной им
   * воронкой). В типе клиента этого ключа нет: он есть только у dry-ответа, а тип описывает
   * обычный. Приводим здесь, а не правим общий контракт.
   */
  const dryData = dry.data as unknown as { fields?: Record<string, unknown> } | undefined
  const dryFields = dryData?.fields
  const dryCategory = dryFields ? String(dryFields.CATEGORY_ID ?? '') : ''
  const categoryId = dryCategory || CORE_CATEGORY_ID
  // Стадию НЕ отправляем: для БП 685 стадия — это вход в маршрутизацию (§4.2Б), и выбрать
  // её при создании значит решить за процесс, куда сделке идти. Bitrix поставит первую
  // стадию воронки сам; какая она — показываем из справочника, чтобы это не было сюрпризом.
  const firstStage = refs.stageOptions(categoryId)[0]

  const checked = dry.isSuccess && canCheck
  const created = create.data

  // Правка формы после успешной проверки обнуляет и проверку, и согласие: подтверждать
  // можно только то, что ядро видело в этом виде.
  const edit = <K extends keyof Form>(k: K) => (v: Form[K]) => {
    if (dry.isSuccess || dry.isError) dry.reset()
    setAgreed(false)
    set(k, v)
  }

  // Единственный экран CRM, который пишет в Bitrix, — и единственный, который надо
  // закрывать целиком. Проверка стоит ДО всех остальных ветвей: внутри ветки «сделка
  // создана» она срабатывала бы уже после записи, то есть никогда не по делу.
  // Ограничение всё равно на сервере; здесь мы объясняем человеку, почему формы нет,
  // вместо отказа в последний момент.
  if (!rights.canWrite) {
    return (
      <div className="card">
        <h1>Новая сделка</h1>
        <div className="notice">
          Создавать сделки может только роль с правом записи. У вашей роли его нет, поэтому
          форма не показана: заполнить её и получить отказ в последний момент хуже, чем
          узнать сразу. Сделку заводят в Bitrix или просят того, у кого право есть.
        </div>
        <p><Link to="/deals">← к списку сделок</Link></p>
      </div>
    )
  }

  // ─── сделка создана и ядро её подтвердило: уводим в карточку ───
  // Никакого «создано» до этого момента: ответ Bitrix не доказывает, что сделка есть в ядре.
  if (created?.deal) {
    return (
      <>
        <div className="page-head"><h1>Сделка создана</h1></div>
        <div className="card">
          <div className="notice">
            Сделка <b>#{created.id}</b> создана в Bitrix и прочитана из ядра
            {created.tries > 1 && ` (ядро подтвердило её с ${created.tries}-й попытки)`}.
            Бизнес-процесс «Процесс для общей сделки» уже запущен — задания появятся у людей
            без вашего участия.
          </div>
          <div className="dl" style={{ marginTop: 12 }}>
            <dt>Название в Bitrix</dt>
            <dd>{String(created.deal.TITLE ?? '')}</dd>
            <dt>Стадия</dt>
            <dd>{refs.stageName(created.deal.STAGE_ID)}</dd>
            <dt>Воронка</dt>
            <dd>{refs.categoryName(created.deal.CATEGORY_ID)}</dd>
            <dt>Сумма</dt>
            <dd>{fmtMoney(created.deal.OPPORTUNITY, created.deal.CURRENCY_ID || 'RUB')}</dd>
          </div>
          <div className="hint" style={{ marginTop: 10 }}>
            Название могло измениться: ядро дописывает к нему номер («#{created.id} / …»),
            а суммы и экономику через несколько секунд пересчитает бизнес-процесс — расхождение
            сразу после создания это норма, а не ошибка.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="primary" onClick={() => navigate(entityPath('deal', created.id))}>
              Открыть карточку сделки
            </button>
            <button onClick={() => { create.reset(); dry.reset(); setForm(EMPTY); setAgreed(false) }}>
              Создать ещё одну
            </button>
          </div>
        </div>
      </>
    )
  }

  // ─── запись прошла, а ядро сделку не отдало ───
  // Самый опасный экран: сделка в Bitrix ЕСТЬ. Слова «не удалось» здесь были бы приглашением
  // создать её второй раз, поэтому их нет.
  if (created && !created.deal) {
    return (
      <>
        <div className="page-head"><h1>Сделка создана, ядро её ещё не отдаёт</h1></div>
        <div className="card">
          <div className="notice err">
            Bitrix сделку <b>создал</b>: id <b>{created.id}</b>. Но перечитать её из ядра не
            удалось за {created.tries} попытки: {created.readbackError || 'ядро не ответило'}.
            <br />
            <b>Не создавайте её заново</b> — она уже есть, и бизнес-процесс по ней уже запущен.
            Ядро догонит её синком; карточка откроется позже.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <Link to={entityPath('deal', created.id)}>Попробовать открыть карточку →</Link>
          </div>
          <div className="hint" style={{ marginTop: 10 }}>
            Так выглядит отставание синка или недоступность ядра — не ошибка записи. Если
            карточка не открывается и через несколько минут, смотреть «Состояние» (/health).
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="page-head">
        <h1>Новая сделка</h1>
        <div className="spacer" />
        <Link to="/deals">← к списку</Link>
      </div>

      {/* Предупреждение стоит ДО формы, а не только перед кнопкой: человек должен понимать,
          во что он вводит данные, ещё до того как начал вводить. */}
      <div className="notice err">
        Это единственный экран CRM, который <b>пишет в боевой Bitrix</b>. Созданная сделка
        сразу запускает бизнес-процесс «Процесс для общей сделки» — он раздаст задания живым
        людям, и отменить это нельзя. Удалить сделку из CRM тоже нельзя: только в Bitrix,
        руками. Черновиков здесь не бывает.
      </div>

      <QueryState isLoading={false} error={null}>
        <div className="card">
          <h2>Поля сделки</h2>

          <div className="filters">
            <div className="f" style={{ flex: 3, minWidth: 280 }}>
              <label htmlFor="nd-title">{f.titleOr('TITLE', 'Название')} *</label>
              <input id="nd-title" value={form.title} maxLength={TITLE_MAX + 50}
                     placeholder="Сервис/тх Название/что делаем"
                     onChange={(e) => edit('title')(e.target.value)} />
            </div>

            <div style={{ flex: 2, minWidth: 240 }}>
              <EntityPicker kind="company" id="nd-company"
                            label={`${f.titleOr('COMPANY_ID', 'Компания')} *`}
                            value={form.company} onChange={edit('company')} />
            </div>

            <div className="f" style={{ minWidth: 200 }}>
              <label htmlFor="nd-assigned">{f.titleOr('ASSIGNED_BY_ID', 'Ответственный')} *</label>
              <select id="nd-assigned" value={form.assigned}
                      disabled={refs.users.length === 0}
                      onChange={(e) => edit('assigned')(e.target.value)}>
                <option value="">выберите сотрудника</option>
                {/* Только действующие: назначить ответственным уволенного — это создать
                    сделку, по которой заданиям БП не к кому прийти. */}
                {refs.users.filter((u) => u.active).map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>

            <div className="f" style={{ minWidth: 160 }}>
              <label htmlFor="nd-sum">{f.titleOr('OPPORTUNITY', 'Сумма')}, ₽ *</label>
              <input id="nd-sum" value={form.opportunity} inputMode="decimal"
                     onChange={(e) => edit('opportunity')(e.target.value)} />
            </div>

            <div style={{ flex: 2, minWidth: 240 }}>
              <EntityPicker kind="vessel" id="nd-vessel"
                            label={f.titleOr(DEAL_VESSEL_FIELD, 'Судно')}
                            value={form.vessel} onChange={edit('vessel')} />
            </div>
          </div>

          {/* Воронка — обязательная часть сделки, но выбора у неё нет, и притворяться
              выпадающим списком было бы обманом. */}
          <div className="dl" style={{ marginTop: 14 }}>
            <dt>{f.titleOr('CATEGORY_ID', 'Воронка')}</dt>
            <dd>
              <b>{refs.categoryName(categoryId)}</b>
              {' '}<span className="muted">— ставит ядро, выбора нет</span>
              {f.readonly('CATEGORY_ID') && (
                <div className="hint">
                  Почему: {f.readonlyReason('CATEGORY_ID')}. Смена воронки сбрасывает стадию,
                  поэтому ядро отвергает запрос, в котором это поле вообще есть, и подставляет
                  «{refs.categoryName(CORE_CATEGORY_ID)}» само.
                </div>
              )}
            </dd>
            <dt>{f.titleOr('STAGE_ID', 'Стадия')}</dt>
            <dd>
              {firstStage
                ? <>первая стадия воронки — <b>{firstStage.name}</b></>
                : <span className="muted">справочник стадий не пришёл — стадию поставит Bitrix</span>}
              <div className="hint">
                Стадию мы не отправляем: для бизнес-процесса это вход в маршрутизацию, а не
                статус. Выбрать её при создании значило бы решить за процесс, куда сделке идти.
              </div>
            </dd>
            <dt>{f.titleOr('CURRENCY_ID', 'Валюта')}</dt>
            <dd>RUB <span className="muted">— как при создании сделки с портала 55</span></dd>
          </div>

          {!f.loaded && (
            <div className="hint" style={{ marginTop: 10 }}>
              Справочник полей сделки из ядра не пришёл: названия показаны нашими словами,
              а пометки «писать нельзя» взять неоткуда. Проверку на стороне ядра это не
              отменяет — закрытое поле оно отвергнет само.
            </div>
          )}
          {f.loaded && f.stale && (
            <div className="hint" style={{ marginTop: 10 }}>
              Справочник полей давно не обновлялся: если поле недавно закрыли для записи,
              форма может об этом не знать. Пробная отправка спросит ядро напрямую.
            </div>
          )}
          {refs.users.length === 0 && (
            <div className="hint" style={{ marginTop: 10 }}>
              Список сотрудников из ядра не пришёл — ответственного выбрать нечем, и создавать
              сделку без него нельзя.
            </div>
          )}
        </div>

        {/* ─── шаг 1: проверка на стороне ядра ─── */}
        <div className="card">
          <h2>Шаг 1. Проверка</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Пробная отправка: ядро проверит поля тем же кодом, что при настоящей записи, и
            вернёт, что именно записало бы. В Bitrix при этом <b>не пишется ничего</b>.
          </p>

          {problems.length > 0 && (
            <ul className="plain-list">
              {problems.map((p) => <li key={p} className="error">{p}</li>)}
            </ul>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <button onClick={() => { setAgreed(false); dry.mutate(fields) }}
                    disabled={!canCheck || dry.isPending}>
              {dry.isPending ? 'Проверяю…' : 'Проверить в ядре'}
            </button>
            {checked && <span className="badge won">проверено</span>}
          </div>

          {/* Ошибку ядра показываем ТЕКСТОМ. «Что-то пошло не так» здесь бесполезно:
              за отказом обычно стоит конкретное поле из блок-листа, и это надо прочитать. */}
          {dry.isError && (
            <div className="notice err" style={{ marginTop: 12 }}>
              Ядро отказало: {(dry.error as Error).message}
            </div>
          )}

          {checked && dryFields && (
            <>
              <div className="hint" style={{ marginTop: 12 }}>
                Ядро запишет ровно эти поля (воронку в списке дописало оно само):
              </div>
              {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
              <div className="table-wrap cards" style={{ marginTop: 6 }}>
                <table>
                  <thead>
                    <tr><th>Поле</th><th className="nowrap">Код</th><th>Значение</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(dryFields).map(([code, v]) => (
                      <tr key={code}>
                        <td data-label="Поле">{f.titleOr(code, code)}</td>
                        <td className="mono nowrap" data-label="Код">{code}</td>
                        <td data-label="Значение">{String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* ─── шаг 2: запись ─── */}
        <div className="card">
          <h2>Шаг 2. Запись в боевой Bitrix</h2>

          {!checked ? (
            <p className="muted" style={{ marginTop: 0 }}>
              Сначала проверка. Кнопка записи появляется только после того, как ядро
              подтвердило поля, — чтобы «Создать» нельзя было нажать вслепую.
            </p>
          ) : (
            <>
              <div className="notice err">
                Сейчас в Bitrix появится сделка <b>«{title.slice(0, TITLE_MAX)}»</b>
                {' '}на {fmtMoney(amount ?? 0)} за компанией <b>{form.company?.title}</b>,
                ответственный — <b>{refs.userName(form.assigned)}</b>.
                <br />
                На её появление подписан бизнес-процесс <b>«Процесс для общей сделки»</b>:
                он сработает сразу и <b>раздаст задания живым людям</b> — они увидят их в
                Bitrix как обычную рабочую задачу. Ни отменить процесс, ни удалить сделку
                из CRM нельзя. Если сделка создана по ошибке, разбираться придётся людям,
                которым уже пришло задание.
              </div>

              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12 }}>
                <input type="checkbox" checked={agreed} style={{ width: 'auto', marginTop: 3 }}
                       onChange={(e) => setAgreed(e.target.checked)} />
                <span>
                  Я понимаю, что это запись в боевой Bitrix, и что бизнес-процесс раздаст
                  задания людям.
                </span>
              </label>

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="primary" disabled={!agreed || create.isPending}
                        onClick={() => create.mutate(fields)}>
                  {create.isPending ? 'Создаю и перечитываю из ядра…' : 'Создать сделку в Bitrix'}
                </button>
                <button disabled={create.isPending}
                        onClick={() => { dry.reset(); setAgreed(false) }}>
                  Вернуться к правке
                </button>
              </div>

              {create.isError && (
                <div className="notice err" style={{ marginTop: 12 }}>
                  Записать не удалось: {(create.error as Error).message}
                  <div className="hint" style={{ marginTop: 6 }}>
                    Текст — от ядра или от Bitrix, без пересказа. Если он начинается с
                    «Bitrix:» — отказал сам Bitrix, и сделки не появилось.
                  </div>
                </div>
              )}

              <div className="hint" style={{ marginTop: 10 }}>
                После записи CRM перечитает сделку из ядра и только тогда скажет «создана».
                Пока ядро не подтвердило — «создано» вы не увидите.
              </div>
            </>
          )}
        </div>

        {/* ─── чего на этом экране нет ─── */}
        <div className="card">
          <h2>Чего здесь нет и почему</h2>
          <ul className="plain-list">
            <li className="muted">
              <b>Полей, которые пишут бизнес-процессы.</b> Ядро закрывает их блок-листом и
              помечает в справочнике; форма показывает только открытые.
              {blockedAll.length > 0 ? (
                <>
                  {' '}Сейчас по справочнику полей сделки закрыто{' '}
                  <b>{blockedAll.reduce((s, g) => s + g.codes.length, 0)}</b> из {f.list.length}:
                  <ul className="plain-list" style={{ marginTop: 6 }}>
                    {blockedAll.map((g) => (
                      <li key={g.reason}>
                        {g.reason} — {g.codes.length}
                        <div className="mono" style={{ fontSize: 11 }}>
                          {g.codes.slice(0, 6).join(', ')}
                          {g.codes.length > 6 && ` … и ещё ${g.codes.length - 6}`}
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                ' Справочник полей из ядра не пришёл, поэтому список закрытых полей показать'
                + ' нечем — но ядро всё равно отвергнет запрос с таким полем.'
              )}
            </li>
            <li className="muted">
              <b>Выбора воронки.</b> Ядро кладёт все созданные с портала сделки в
              «{refs.categoryName(CORE_CATEGORY_ID)}» — так решено 14.07.2026, и поле воронки
              в блок-листе. Нужна другая воронка — сделку переносят в Bitrix.
            </li>
            <li className="muted">
              <b>Выбора стадии.</b> Стадия — вход в маршрутизацию бизнес-процесса, а не
              статус. Сделка встаёт на первую стадию воронки, дальше её двигает процесс.
            </li>
            <li className="muted">
              <b>Остальных полей сделки ({Math.max(f.list.length - WRITTEN_FIELDS.length, 0)} с
              лишним).</b> Контакт, сроки, комментарий, договор, подразделение — всё это на
              портале 55 в форме есть. Здесь их нет намеренно: первая запись из CRM в боевой
              Bitrix должна быть минимальной и целиком проверяемой. Дозаполняют сделку в её
              карточке.
            </li>
            <li className="muted">
              <b>Черновиков и отмены.</b> В Bitrix сделка появляется сразу и целиком.
              «Проверить» — единственный способ ничего не создать, и он в шаге 1.
            </li>
          </ul>
        </div>
      </QueryState>
    </>
  )
}
